<script setup lang="ts">
import { computed } from 'vue';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

interface BreadcrumbItem {
  label: string;
  link: string | null;
}

const route = useRoute();
const { t, te } = useI18n();

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const breadcrumbs: BreadcrumbItem[] = [];

  // Use route.matched instead of trying to reconstruct paths
  route.matched.forEach((matchedRoute, index) => {
    let label = '';
    if (typeof matchedRoute.meta.breadcrumb === 'string') {
      const key = matchedRoute.meta.breadcrumb;
      label = te(key) ? t(key) : key;
    } else {
      // Fallback: use the path segment or route name
      const pathSegment = matchedRoute.path.split('/').filter(Boolean).pop();
      label = pathSegment ? pathSegment.charAt(0).toUpperCase() + pathSegment.slice(1) : 'Unknown';
    }

    let canNavigate = !!matchedRoute.components && index < route.matched.length - 1; // Only allow navigation to parent routes
    const linkPath = index < route.matched.length - 1 ? matchedRoute.path : null;

    breadcrumbs.push({
      label: label,
      link: canNavigate ? linkPath : null,
    });
  });

  return breadcrumbs;
});
</script>

<template>
  <Breadcrumb>
    <BreadcrumbList v-if="breadcrumbItems.length">
      <BreadcrumbItem v-for="(item, index) in breadcrumbItems" :key="index">
        <BreadcrumbLink v-if="item.link" :href="item.link">{{ item.label }}</BreadcrumbLink>
        <BreadcrumbPage v-else>{{ item.label }}</BreadcrumbPage>
        <BreadcrumbSeparator v-if="index < breadcrumbItems.length - 1" />
      </BreadcrumbItem>
    </BreadcrumbList>
  </Breadcrumb>
</template>
